(() => {
  class LazyProductTabs {
    constructor(root) {
      this.root = root;
      this.loadingTabs = new Map();
      this.onBreakpointChange = this.onBreakpointChange.bind(this);
    }

    get dataset() {
      return this.root.dataset;
    }

    get id() {
      return this.root.id;
    }

    querySelector(selector) {
      return this.root.querySelector(selector);
    }

    querySelectorAll(selector) {
      return this.root.querySelectorAll(selector);
    }

    destroy() {
      document.removeEventListener('matchMobile', this.onBreakpointChange);
      document.removeEventListener('unmatchMobile', this.onBreakpointChange);
    }

    init() {
      if (this.initialized || !window.MinimogTheme?.Tabs) return;
      this.initialized = true;
      this.selectors = {
        loadMoreBtn: '[data-load-more-product]',
        productsContainer: '[data-products-container]'
      };
      this.sliderEnabled = this.dataset.enableSlider === 'true';
      this.mobileSliderDisable = this.dataset.mobileDisableSlider === 'true';
      this.buttonType = this.dataset.buttonType;
      this.showPagination = this.dataset.showPagination === 'true';
      this.showNavigation = this.dataset.showNavigation === 'true';
      this.items = Number(this.dataset.items) || 4;

      this.querySelectorAll('.m-tab-content[data-lazy-loaded="true"]').forEach((tab) => this.initializeTab(tab));
      document.addEventListener('matchMobile', this.onBreakpointChange);
      document.addEventListener('unmatchMobile', this.onBreakpointChange);
      this.initTabs();
      this.initMobileSelect();

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:block:select', (event) => {
          if (!event.target.classList.contains('m-tab-content')) return;
          const index = Number(event.target.dataset.index);
          this.tabs.setActiveTab(index);
          this.ensureTabLoaded(event.target);
        });
      }
    }

    initTabs() {
      this.tabs = new MinimogTheme.Tabs(this.root, (header) => {
        const tab = this.querySelector(header.getAttribute('href'));
        this.ensureTabLoaded(tab).then(() => this.refreshTab(tab));
      });
    }

    initMobileSelect() {
      const select = this.querySelector('[data-tab-select]');
      if (!select) return;
      select.addEventListener('change', () => {
        this.tabs.setActiveTab(Number(select.value));
        const tab = this.tabs.currentTab;
        this.ensureTabLoaded(tab).then(() => this.refreshTab(tab));
      });
    }

    onBreakpointChange() {
      this.querySelectorAll('.m-tab-content[data-lazy-loaded="true"]').forEach((tab) => {
        if (this.sliderEnabled) this.initSliderByScreenSize(tab);
        if (this.shouldUseLoadMore()) this.initLoadMore(tab);
      });
    }

    shouldUseLoadMore() {
      return this.buttonType === 'load' && (!this.sliderEnabled || (MinimogTheme.config.mqlMobile && this.mobileSliderDisable));
    }

    initializeTab(tab) {
      if (!tab || tab.dataset.lazyInitialized === 'true') return;
      tab.dataset.lazyInitialized = 'true';
      if (this.sliderEnabled) this.initSliderByScreenSize(tab);
      if (this.shouldUseLoadMore()) this.initLoadMore(tab);
      this.initializeHighlights(tab);
      window.MinimogTheme.CompareProduct?.setCompareButtonsState?.();
      window.MinimogTheme.Wishlist?.setWishlistButtonsState?.();
    }

    ensureTabLoaded(tab) {
      if (!tab || tab.dataset.lazyLoaded !== 'false') {
        this.initializeTab(tab);
        return Promise.resolve(tab);
      }
      if (this.loadingTabs.has(tab.id)) return this.loadingTabs.get(tab.id);

      tab.setAttribute('aria-busy', 'true');
      const requestUrl = new URL(tab.dataset.lazyUrl || tab.dataset.url, window.location.origin);
      requestUrl.searchParams.set('section_id', this.id);

      const request = fetch(requestUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const source = new DOMParser().parseFromString(html, 'text/html').getElementById(tab.id);
          if (!source || source.dataset.lazyLoaded !== 'true') throw new Error('Tab content was not returned');
          tab.innerHTML = source.innerHTML;
          ['url', 'totalPages', 'page', 'totalItems', 'enableSlide'].forEach((key) => {
            if (source.dataset[key] !== undefined) tab.dataset[key] = source.dataset[key];
          });
          tab.dataset.lazyLoaded = 'true';
          tab.removeAttribute('aria-busy');
          this.initializeTab(tab);
          return tab;
        })
        .catch((error) => {
          tab.removeAttribute('aria-busy');
          const status = tab.querySelector('[data-tab-loading-status]');
          if (status) status.textContent = 'Products could not be loaded. Please try again.';
          console.error('Product tab loading failed:', error);
          return tab;
        })
        .finally(() => this.loadingTabs.delete(tab.id));

      this.loadingTabs.set(tab.id, request);
      return request;
    }

    initializeHighlights(tab) {
      if (typeof window.createItemHightlights !== 'function') return;
      tab.querySelectorAll('input[id^="params-list-"]').forEach((container) => {
        const productId = container.id.replace('params-list-', '');
        tab.querySelectorAll(`ul.hck-product-highlights-ul-${CSS.escape(productId)}`).forEach((list) => {
          window.createItemHightlights(list, container);
        });
      });
    }

    refreshTab(tab) {
      if (!tab) return;
      const slider = tab.querySelector('.swiper-container');
      slider?.swiper?.update();
      const controls = tab.querySelector('.m-slider-controls');
      const image = slider?.querySelector('.m-image, .m-placeholder-svg');
      if (controls && image) controls.style.setProperty('--offset-top', `${Math.floor(image.clientHeight / 2)}px`);
    }

    initSliderByScreenSize(tab) {
      const wrapper = tab.querySelector('.m-mixed-layout__wrapper');
      if (!wrapper) return;
      const controls = tab.querySelector('.m-slider-controls');
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable) {
        controls?.classList.add('m:hidden');
        wrapper.classList.remove('swiper-container');
        wrapper.swiper?.destroy(false, true);
      } else {
        controls?.classList.remove('m:hidden');
        window.setTimeout(() => this.initSlider(tab));
      }
    }

    initSlider(tab) {
      const productList = tab.querySelector('.m-product-list');
      const wrapper = tab.querySelector('.m-mixed-layout__wrapper');
      const swiperWrapper = tab.querySelector('.swiper-wrapper');
      if (!productList || !wrapper || !swiperWrapper || wrapper.swiper) return;
      const controls = tab.querySelector('.m-slider-controls');
      const previous = controls?.querySelector('.m-slider-controls__button-prev');
      const next = controls?.querySelector('.m-slider-controls__button-next');
      const productCount = swiperWrapper.childElementCount;
      if (this.items >= productCount) {
        controls?.classList.add('m:hidden');
        productList.classList.add('m-mixed-layout--mobile-grid');
        return;
      }

      wrapper.classList.add('swiper-container');
      const slider = new MinimogLibs.Swiper(wrapper, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: false,
        pagination: this.showPagination ? { el: tab.querySelector('.swiper-pagination'), clickable: true } : false,
        breakpoints: {
          768: { slidesPerView: Math.min(3, this.items) },
          992: { slidesPerView: Math.min(4, this.items) },
          1280: { slidesPerView: this.items }
        },
        threshold: 2,
        on: {
          init: () => window.setTimeout(() => this.refreshTab(tab), 200),
          breakpoint: (swiper, options) => {
            if (!controls) return;
            const hasOverflow = productCount > options.slidesPerView;
            controls.classList.toggle('m:hidden', !hasOverflow);
            swiper.allowTouchMove = hasOverflow;
          }
        }
      });
      if (slider && this.showNavigation) {
        previous?.addEventListener('click', () => slider.slidePrev());
        next?.addEventListener('click', () => slider.slideNext());
      }
    }

    initLoadMore(tab) {
      if (tab.dataset.loadMoreInitialized === 'true') return;
      const button = tab.querySelector(this.selectors.loadMoreBtn);
      if (!button || button.tagName === 'A') return;
      tab.dataset.loadMoreInitialized = 'true';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.handleLoadMore(tab, button);
      });
    }

    handleLoadMore(tab, button) {
      const container = tab.querySelector(this.selectors.productsContainer);
      const currentPage = Number(tab.dataset.page) || 1;
      const totalPages = Number(tab.dataset.totalPages) || 1;
      if (!container || currentPage >= totalPages) return;
      button.classList.add('m-spinner-loading');
      const requestUrl = new URL(tab.dataset.url, window.location.origin);
      requestUrl.searchParams.set('page', currentPage + 1);
      requestUrl.searchParams.set('section_id', this.id);
      fetch(requestUrl.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then((response) => {
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          return response.text();
        })
        .then((html) => {
          const source = new DOMParser().parseFromString(html, 'text/html').getElementById(tab.id);
          const products = source?.querySelector(this.selectors.productsContainer);
          if (!products) throw new Error('More products were not returned');
          Array.from(products.children).forEach((product) => container.appendChild(product));
          tab.dataset.page = String(currentPage + 1);
          this.initializeHighlights(tab);
          if (currentPage + 1 >= totalPages) button.remove();
        })
        .catch((error) => console.error('Loading more products failed:', error))
        .finally(() => button.isConnected && button.classList.remove('m-spinner-loading'));
      button.blur();
    }
  }

  const controllers = new WeakMap();
  window.initLazyProductTabs = (root) => {
    if (controllers.has(root)) return;
    const controller = new LazyProductTabs(root);
    controllers.set(root, controller);
    controller.init();
  };
  window.destroyLazyProductTabs = (root) => {
    controllers.get(root)?.destroy();
    controllers.delete(root);
  };
  document.querySelectorAll('m-product-tabs').forEach(window.initLazyProductTabs);
  window.dispatchEvent(new CustomEvent('product-tabs-lazy:ready'));
})();
